import { useEffect, useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, LinearProgress, TextField, Box, Button } from "@material-ui/core";
import { Connection, PublicKey, clusterApiUrl, Transaction, SystemProgram } from "@solana/web3.js";

const useStyles = makeStyles((theme) => ({
  presaleSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "2rem",
    backgroundColor: "rgb(10, 20, 40)", // Dark background to match the navbar
    minHeight: "70vh",
  },
  title: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    marginBottom: "2rem",
    color: "#ffffff", // White title for visibility
  },
  progressBarContainer: {
    width: "80%", // Relative width for responsiveness
    marginBottom: "2rem", // Increased margin for spacing
  },
  progressLabel: {
    textAlign: "center",
    fontSize: "1.2rem",
    color: "#ffffff", // White for progress label
    marginBottom: "1rem",
  },
  inputContainer: {
    display: "flex",
    alignItems: "center",
    gap: "1rem", // Adds space between textfield and button
    marginBottom: "2rem", // Adds space below input container
    marginTop: "3rem",
  },
  textField: {
    width: "40%", // Adjusted to a smaller width
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#ff0080", // Neon pink border on input field
      },
      "&:hover fieldset": {
        borderColor: "#ff4da6", // Lighter neon pink on hover
      },
      "&.Mui-focused fieldset": {
        borderColor: "#ff0080", // Neon pink on focus
      },
    },
  },
  buyButton: {
    fontSize: "1.2rem",
    padding: "0.75rem 2rem",
    background: "#ff0080", // Neon pink button
    color: "#ffffff", // White text for contrast
    borderRadius: "0.5rem",
    whiteSpace: "nowrap",
    "&:hover": {
      background: "#ff4da6", // Lighter neon pink on hover
    },
  },
  priceText: {
    fontSize: "1.2rem",
    color: "#ffffff", // White text for price info
    textAlign: "center",
  },
}));

const PresaleSection = () => {
  const classes = useStyles();
  const presaleWallet = "BPjJ9k8UiriwZirnRQqLp2vfVW2vxZs8ZNLbuVF1RnxF";
  const goalAmount = 500; // Presale goal in SOL
  const [solAmount, setSolAmount] = useState("1"); // Default value for the input
  const [walletAddress, setWalletAddress] = useState(null); // User's wallet address
  const [raisedAmount, setRaisedAmount] = useState(0); // User's SOL balance
  const [provider, setProvider] = useState(null); // Phantom wallet provider

  // Memoize the connection object so it's not recreated on every render
  const connection = useMemo(() => new Connection(clusterApiUrl("devnet"), "confirmed"), []);

  
  useEffect(() => {
    if (window.solana && window.solana.isPhantom) {
      const provider = window.solana;
      setProvider(provider);

      provider.on("connect", async () => {
        const publicKey = provider.publicKey.toString();
        setWalletAddress(publicKey);

        // Fetch wallet balance when connected
        const balance = await connection.getBalance(new PublicKey(presaleWallet));
        setRaisedAmount(balance / 1e9); // Convert lamports to SOL
      });
    }
  }, [connection]);

  // Handle input change for SOL amount
  const handleInputChange = (event) => {
    setSolAmount(event.target.value); // Update state when input changes
  };

  // Handle Buy button click
  const handleBuyClick = async () => {
    if (provider && walletAddress) {
      // Fetch the recent blockhash
      const { blockhash } = await connection.getRecentBlockhash();
      const transaction = new Transaction({
        recentBlockhash: blockhash, // Add the blockhash to the transaction
        feePayer: new PublicKey(walletAddress), // Set the fee payer (sender)
      }).add(
        SystemProgram.transfer({
          fromPubkey: new PublicKey(walletAddress),
          toPubkey: new PublicKey(presaleWallet),
          lamports: parseFloat(solAmount) * 1e9, // Convert SOL to lamports
        })
      );

      // Sign and send transaction
      try {
        const signature = await provider.signAndSendTransaction(transaction, connection);
        console.log("Transaction sent with signature:", signature);
      } catch (error) {
        console.error("Error sending transaction:", error);
      }
    }
  };

  const progress = (raisedAmount / goalAmount) * 100; // Percentage of presale raised

  return (
    <Box className={classes.presaleSection}>
      <Typography className={classes.title}>$MFIT COIN PRESALE IS LIVE</Typography>

      <Box className={classes.progressBarContainer}>
        <Typography className={classes.progressLabel}>
          {raisedAmount}/{goalAmount} SOL raised
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          color="secondary"
          style={{ height: "1rem", borderRadius: "1rem" }}
        />
      </Box>

      <Box className={classes.inputContainer}>
        <TextField
          className={classes.textField}
          label="Enter SOL Amount"
          variant="outlined"
          type="number"
          value={solAmount} // Bind value to state
          onChange={handleInputChange} // Update state on change
          InputProps={{
            step: "0.01",
          }}
          fullWidth
        />
        <Button className={classes.buyButton} onClick={handleBuyClick}>
          BUY WITH {solAmount || "X"} SOL
        </Button>
      </Box>

      <Typography className={classes.priceText}>
        PRESALE PRICE: 1 SOL = 1000 $MFIT TOKENS
      </Typography>
    </Box>
  );
};

export default PresaleSection;
